import type { RootOptions } from 'react-dom/client';
import { sanitizeText } from '../../api/urlSanitizer';

// AppLogger + AppErrorReporter and the global error hooks (app_logger.dart,
// app_error_reporter.dart, main.dart:117-150; spec 11 §1.1, §11; DATA-020,
// SHELL-026, alt: window.onerror + unhandledrejection). Every entry is one
// `[app-log]` JSON line `{ts, level INFO|WARN|ERROR, category, operation,
// message, details?}` with credentials redacted from any URL it mentions
// (media URLs carry `?token=`). Errors carry Flutter's AppError metadata in
// `details`, and can be throttled (identical entries within 8 s are dropped).
// Logging never throws.

const DEV = import.meta.env.DEV;
/** Debug builds also print the first stack lines of an error, like Flutter's AppLogger. */
const STACK_LINES = 20;
/** Flutter `AppLogger._errorThrottleWindow`. */
export const ERROR_THROTTLE_MS = 8000;

export type LogLevel = 'INFO' | 'WARN' | 'ERROR';
export type AppErrorCategory = 'network' | 'auth' | 'parsing' | 'storage' | 'playback' | 'unknown';
export type AppErrorAction = 'retry' | 'relogin' | 'reDownload' | 'checkConnection' | 'none';

/** The AppError fields Flutter's `AppLogger.error` merges into `details`. */
export interface AppErrorMetadata {
  errorCategory: AppErrorCategory;
  errorCode: string;
  statusCode: number | null;
  isRetryable: boolean;
  recommendedAction: AppErrorAction;
}

export interface ReportErrorOptions {
  /** Drop the entry when an identical one (category, operation, code, message) was logged in the last 8 s. */
  throttle?: boolean;
}

function describe(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) return { message: `${error.name}: ${error.message}`, stack: error.stack };
  if (typeof error === 'string') return { message: error };
  try {
    return { message: JSON.stringify(error) ?? String(error) };
  } catch {
    return { message: String(error) };
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

const CATEGORIES = new Set<string>(['network', 'auth', 'parsing', 'storage', 'playback', 'unknown']);
const ACTIONS = new Set<string>(['retry', 'relogin', 'reDownload', 'checkConnection', 'none']);

/**
 * Flutter's `mapExceptionToAppError` for the web's error shapes: an AppError-like
 * object keeps its own fields; an `AbsError` (HTTP status, or a network failure
 * with status 0) maps like a DioException; aborts, storage quota, JSON parse and
 * media errors get their categories; anything else is `unknown_<operation>`.
 */
export function appErrorMetadata(error: unknown, operation?: string): AppErrorMetadata {
  const r = record(error);
  const name = error instanceof Error || error instanceof DOMException ? error.name : '';
  const message = error instanceof Error ? error.message : '';
  const statusCode = typeof r?.statusCode === 'number' ? r.statusCode : typeof r?.status === 'number' ? r.status : null;

  // Already an AppError (features may log Flutter-shaped errors).
  if (r && typeof r.category === 'string' && CATEGORIES.has(r.category)) {
    const isRetryable = typeof r.isRetryable === 'boolean' ? r.isRetryable : false;
    return {
      errorCategory: r.category as AppErrorCategory,
      errorCode: typeof r.code === 'string' ? r.code : String(r.category),
      statusCode,
      isRetryable,
      recommendedAction: typeof r.recommendedAction === 'string' && ACTIONS.has(r.recommendedAction) ? (r.recommendedAction as AppErrorAction) : 'none',
    };
  }
  if (name === 'AbortError') {
    return { errorCategory: 'network', errorCode: 'network_cancelled', statusCode, isRetryable: true, recommendedAction: 'retry' };
  }
  if (name === 'TimeoutError') {
    return { errorCategory: 'network', errorCode: 'network_timeout', statusCode, isRetryable: true, recommendedAction: 'retry' };
  }
  if (statusCode !== null) {
    if (r?.isNetworkError === true || statusCode === 0) {
      return /too long|timed? ?out/i.test(message)
        ? { errorCategory: 'network', errorCode: 'network_timeout', statusCode: null, isRetryable: true, recommendedAction: 'retry' }
        : { errorCategory: 'network', errorCode: 'network_connection_error', statusCode: null, isRetryable: true, recommendedAction: 'checkConnection' };
    }
    if (statusCode === 401 || statusCode === 403) {
      return { errorCategory: 'auth', errorCode: `auth_http_${statusCode}`, statusCode, isRetryable: false, recommendedAction: 'relogin' };
    }
    if (statusCode === 404) {
      return { errorCategory: 'network', errorCode: 'http_404', statusCode, isRetryable: false, recommendedAction: 'none' };
    }
    const retryable = statusCode >= 500 || statusCode === 408 || statusCode === 429;
    return { errorCategory: 'network', errorCode: `http_${statusCode}`, statusCode, isRetryable: retryable, recommendedAction: retryable ? 'retry' : 'none' };
  }
  if (error instanceof TypeError && /fetch|network|load failed/i.test(message)) {
    return { errorCategory: 'network', errorCode: 'network_connection_error', statusCode, isRetryable: true, recommendedAction: 'checkConnection' };
  }
  if (name === 'QuotaExceededError' || name === 'NotReadableError' || name === 'InvalidStateError') {
    return { errorCategory: 'storage', errorCode: 'storage_failure', statusCode, isRetryable: false, recommendedAction: 'none' };
  }
  if (error instanceof SyntaxError) {
    return { errorCategory: 'parsing', errorCode: 'data_format', statusCode, isRetryable: false, recommendedAction: 'none' };
  }
  if (typeof MediaError !== 'undefined' && error instanceof MediaError) {
    return { errorCategory: 'playback', errorCode: `media_error_${error.code}`, statusCode, isRetryable: true, recommendedAction: 'retry' };
  }
  return { errorCategory: 'unknown', errorCode: operation ? `unknown_${operation}` : 'unknown', statusCode, isRetryable: true, recommendedAction: 'retry' };
}

/** Flutter `_stringifyDetails`: primitives and null stay, strings are redacted, anything else becomes text. */
function stringifyDetails(details: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(details).map(([key, value]) => {
      if (value === null || value === undefined || typeof value === 'number' || typeof value === 'boolean') return [key, value ?? null];
      if (typeof value === 'string') return [key, sanitizeText(value)];
      let text: string;
      try {
        text = value instanceof Error ? `${value.name}: ${value.message}` : (JSON.stringify(value) ?? String(value));
      } catch {
        text = String(value);
      }
      return [key, sanitizeText(text)];
    }),
  );
}

const CONSOLE_METHOD = { INFO: 'info', WARN: 'warn', ERROR: 'error' } as const;

function writeEntry(level: LogLevel, category: string, operation: string, message: string, details: Record<string, unknown> | undefined, extra: string[] = []): void {
  const entry = {
    ts: new Date().toISOString(),
    level,
    category,
    operation,
    message: sanitizeText(message),
    ...(details && Object.keys(details).length > 0 ? { details: stringifyDetails(details) } : {}),
  };
  console[CONSOLE_METHOD[level]](`[app-log] ${JSON.stringify(entry)}`, ...extra);
}

const lastErrorAt = new Map<string, number>();

/** True when an identical error was logged within the throttle window (Flutter `_isThrottled`); records it otherwise. */
function isThrottled(key: string, now: number): boolean {
  const last = lastErrorAt.get(key);
  if (last !== undefined && now - last < ERROR_THROTTLE_MS) return true;
  lastErrorAt.set(key, now);
  return false;
}

/** Forgets throttled keys (tests). */
export function resetLogThrottle(): void {
  lastErrorAt.clear();
}

/**
 * Logs `error` as an ERROR entry (Flutter `AppErrorReporter.report` →
 * `AppLogger.error`): `details` gains errorCategory, errorCode, statusCode,
 * isRetryable and recommendedAction. With `throttle`, an identical
 * category|operation|code|message entry within 8 s is dropped. The message is
 * the (redacted) error text rather than Flutter's user-facing copy: the web
 * has no debugger attached to recover the original exception.
 */
export function reportError(
  error: unknown,
  operation: string,
  category = 'app',
  details?: Record<string, unknown>,
  options: ReportErrorOptions = {},
): void {
  try {
    const { message, stack } = describe(error);
    const metadata = appErrorMetadata(error, operation);
    if (options.throttle && isThrottled(`${category}|${operation}|${metadata.errorCode}|${sanitizeText(message)}`, Date.now())) return;
    const lines = DEV && stack ? [sanitizeText(stack.split('\n').slice(0, STACK_LINES).join('\n'))] : [];
    writeEntry('ERROR', category, operation, message, { ...details, ...metadata }, lines);
  } catch {
    // Logging must never become the next error.
  }
}

/**
 * Routes uncaught script errors and unhandled promise rejections through
 * `reportError` and marks them handled, so the browser does not also print
 * them unredacted (Flutter's `PlatformDispatcher.onError` returns true).
 */
export function installGlobalErrorHooks(target: Window = window): () => void {
  const onError = (event: ErrorEvent) => {
    const source = event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined;
    reportError(event.error ?? event.message, 'window.onerror', 'global', source ? { source } : undefined);
    event.preventDefault();
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    reportError(event.reason, 'unhandledrejection', 'global');
    event.preventDefault();
  };
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}

function renderDetails(info: { componentStack?: string | undefined }): Record<string, unknown> | undefined {
  return DEV && info.componentStack ? { componentStack: info.componentStack } : undefined;
}

/** `createRoot` options: React's own error logging goes through the same redacting reporter. */
export const rootErrorOptions: Pick<RootOptions, 'onCaughtError' | 'onUncaughtError' | 'onRecoverableError'> = {
  onCaughtError: (error, info) => reportError(error, 'react.caught', 'render', renderDetails(info)),
  onUncaughtError: (error, info) => reportError(error, 'react.uncaught', 'render', renderDetails(info)),
  onRecoverableError: (error, info) => reportError(error, 'react.recoverable', 'render', renderDetails(info)),
};
